import { DateTime, Interval } from 'luxon'
import { Box, Pressable, VStack } from 'native-base'
import { useContext, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useLazyQuery } from '@apollo/client'

import ExpandButtonSVG from '../assets/ExpandButtonSVG'
import { AppContext } from '../context/AppContext'
import { AuthContext } from '../context/AuthContext'
import { OverlayState } from '../context/OverlayContext'
import {
  GET_PROOF_OF_SERVICE_URLS,
  GetProofOfServiceUrlsQuery,
  GetProofOfServiceUrlsQueryVariables,
  Order,
  PackageEventOperations,
} from '@consumer/graphql'
import { DelayAlert, OrderInfoHeader } from '@consumer/ui'

import { trackEvent } from '../utils/analytics-v2'
import { DeliveryAddress } from './DeliveryAddress'
import { OrderMap } from './Map/OrderMap'
import PackageInfoWithCollapsibleEventLog from './PackageInfoWithCollapsibleEventLog'

interface ISearchResultProps {
  order: Order
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBackToSearch: (e: any) => void
}

export const SearchResult = ({ order, onBackToSearch }: ISearchResultProps) => {
  const { toggleModal, toggleMapModal, adjustGeopinToast, handleSetAdjustGeopinToast } = useContext(OverlayState)
  const { firebaseUser, user } = useContext(AuthContext)
  const { handleSetProofOfServicePhotos } = useContext(AppContext)
  const navigate = useNavigate()
  const { trackingId } = useParams()
  const [queryProofOfServiceUrlsByPackageIds, proofOfServicePhotoUrls] = useLazyQuery<
    GetProofOfServiceUrlsQuery,
    GetProofOfServiceUrlsQueryVariables
  >(GET_PROOF_OF_SERVICE_URLS, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  })
  const hasAlert = !!order.alert
  useEffect(() => {
    const deliveredPackageIds =
      order?.packages
        // Fetch POD URLs for delivered and misdelivered packages. We surface the POD on the
        // tracking page header for any misdelivery reason so consumers can see what was captured
        // at the time of the (mis)delivery.
        .filter(
          p =>
            p.eventLog[0]?.operation === PackageEventOperations.Delivered ||
            p.eventLog[0]?.operation === PackageEventOperations.Misdelivered
        )
        // ignore lint rule as we only want to return packageIds within the interval
        .filter(p => {
          const deliveredTimeStamp = p?.eventLog[0]?.timestamp
          const interval = Interval.fromDateTimes(
            DateTime.fromISO(deliveredTimeStamp).setZone((p.timeZone ?? order.timeZone)!),
            DateTime.fromISO(deliveredTimeStamp)
              .setZone((p.timeZone ?? order.timeZone)!)
              .plus({ days: 90, hours: 23, minutes: 59 })
          )
          const now = DateTime.now().setZone((p.timeZone ?? order.timeZone)!)
          const todayWithinInterval = interval.contains(now)
          if (todayWithinInterval) {
            return p.packageId
          }
        }) || []
    if (deliveredPackageIds.length > 0) {
      const packageIds: string[] = deliveredPackageIds.map(p => p.packageId)
      queryProofOfServiceUrlsByPackageIds({ variables: { packageIds } })
    }
  }, [order, queryProofOfServiceUrlsByPackageIds])

  const hasProofOfServicePhotosWithLocation: boolean = useMemo(() => {
    return proofOfServicePhotoUrls?.data?.proofOfServicePhotoUrls?.some(p => p.location?.lat && p.location.lng) ?? false
  }, [proofOfServicePhotoUrls])

  useEffect(() => {
    // set proof of delivery photos in AppContext for full screen map
    if (proofOfServicePhotoUrls?.data?.proofOfServicePhotoUrls?.length) {
      handleSetProofOfServicePhotos(proofOfServicePhotoUrls?.data?.proofOfServicePhotoUrls)
    }
  }, [proofOfServicePhotoUrls, handleSetProofOfServicePhotos, adjustGeopinToast, handleSetAdjustGeopinToast])

  const handleAddInstructions = () => {
    trackEvent('view_instructions', {
      trigger: 'manual',
      veho_client_id: order.clientId ?? undefined,
    })
    toggleModal({ showModal: true, auth_flow: 'instructions' })
  }

  const handleEditAddress = () => {
    toggleModal({ showModal: true, auth_flow: 'edit_address' })
  }

  const handleMapExpandToggle = () => {
    if (firebaseUser || (order?.address?.street && order?.address?.name)) {
      toggleMapModal({ showToastAfterClose: false })
    } else {
      toggleModal({ showModal: true, auth_flow: 'general' })
    }
  }

  const handleEditBusinessHours = () => {
    if (trackingId) {
      navigate(`/trackingId/${trackingId}/hours`, {})
    } else if (order?.packages?.[0]?.trackingId) {
      navigate(`/trackingId/${order.packages[0].trackingId}/hours`)
    }
  }

  return (
    <Box>
      {hasAlert && order.alert && (
        <Box mb={3}>
          <DelayAlert title={order.alert.title} description={order.alert.body} />
        </Box>
      )}
      {order?.packages?.map((pkg, idx) => {
        return (
          <Box key={`order-pkg-info-${idx}`} mt={idx > 0 ? 2 : 0}>
            <OrderInfoHeader clientName={order.clientDisplayName || order.clientName} trackingId={pkg.trackingId} />
            <PackageInfoWithCollapsibleEventLog
              pkg={pkg}
              showBottomBorder={idx === 0 && order?.packages?.length > 1}
              proofOfServicePhotos={proofOfServicePhotoUrls?.data?.proofOfServicePhotoUrls}
            />
          </Box>
        )
      })}
      <VStack alignItems={'center'}>
        {order?.instructionEditsAllowed ? (
          <OrderMap
            order={order}
            toggleMap={handleMapExpandToggle}
            proofOfServicePhotos={proofOfServicePhotoUrls?.data?.proofOfServicePhotoUrls}
            userAuthed={!!firebaseUser}
            acceptedTermsAndConditions={!!user?.agreedToTermsAndConditions}
            showMarkerPopupForAddress={false}
            showGeocodeCorrection={true}
            adjustGeopinToast={adjustGeopinToast}
            handleSetAdjustGeopinToast={handleSetAdjustGeopinToast}
            useUpdateNormalizedAddress={false}
          />
        ) : (
          <Box style={{ flex: 1, marginBottom: 50 }}></Box>
        )}
        {(firebaseUser ?? (order?.address?.street && order?.address?.name)) && (
          <Box zIndex={2000} style={{ position: 'absolute', right: 18, top: 18 }}>
            <Pressable onPress={handleMapExpandToggle}>
              <ExpandButtonSVG />
            </Pressable>
          </Box>
        )}
        <Box display={'flex'} flexDir={'column'} alignItems={'center'} zIndex={2000}>
          <DeliveryAddress
            order={order}
            editAddress={handleEditAddress}
            addInstructions={handleAddInstructions}
            styleProps={{ minWidth: '350px' }}
            toggleMap={handleMapExpandToggle}
            toggleModal={toggleModal}
            variant="full"
            showViewPackageLocationButton={true}
            onEditBusinessHours={handleEditBusinessHours}
            onBackToSearch={onBackToSearch}
            hasProofOfServicePhotosWithLocation={hasProofOfServicePhotosWithLocation}
          />
        </Box>
      </VStack>
    </Box>
  )
}
